import Store from '@ember-data/store';
import Controller from '@ember/controller';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { cached, tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import type { IIndexable } from 'donor-elf-web/lib/common-types';
import {
  type AppliedPropertyFilter,
  ContactIdsIntersection,
  deserializeAppliedFilters,
  type DistanceFromVal,
  type PropertyFilterInfoForQueryFilter,
  serializeAppliedFilters,
} from 'donor-elf-web/lib/filter-utils-new';
import objectUtils from 'donor-elf-web/lib/object-utils';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import ContactsExportService from 'donor-elf-web/services/contacts-export';
import FormatDateService from 'donor-elf-web/services/format-date-service';
import FileSaver from 'file-saver';
import Papa from 'papaparse';

export default class DonorsByTimeFrameController extends Controller {
  @service declare contactsCache: ContactsCache;
  @service declare contactsExport: ContactsExportService;
  @service declare formatDateService: FormatDateService;
  @service declare router: RouterService;
  @service declare store: Store;

  queryParams = ['filters'];

  @tracked appliedFilters?: AppliedPropertyFilter[];
  @tracked error?: string;
  @tracked filters?: string;
  @tracked items?: any[];
  @tracked itemsByStateSortBy = ['amount:desc'];
  @tracked loading = false;
  @tracked sortBy = ['record.totalDonations:desc'];

  async load() {
    this.error = undefined;
    this.items = undefined;
    this.loading = true;

    try {
      const queryFilter: any = {};

      for (const appliedFilter of this.appliedFilters || []) {
        const propertyFilter = this.availablePropertyFilters.find(
          (f) => f.propertyName === appliedFilter.propertyName,
        );

        if (propertyFilter) {
          await propertyFilter.applyToQueryFilter(appliedFilter, queryFilter);
        }
      }

      // for this report, we're filtering on the contact ids after the API response
      // so we don't have to worry about the query string becoming too long if there
      // are hundreds of filteredContactIds
      const filteredContactIds = queryFilter.contactIds;
      if (filteredContactIds) {
        delete queryFilter.contactIds;
      }

      let records = (
        await this.store.query('donor-by-time-frame', {
          filter: objectUtils.decamelizeKeys(queryFilter),
        })
      ).slice();

      if (filteredContactIds) {
        records = records.filter((record) =>
          filteredContactIds.includes(record.contactId),
        );
      }

      this.items = records.map((record) => {
        return {
          contact: this.contactsCache.getById(record.contactId),
          record,
        };
      });
    } catch (error) {
      this.error = error;
    } finally {
      this.loading = false;
    }
  }

  get actionsMenuItems() {
    return [
      {
        label: 'Create Group',
        svgJar: 'users',
        onClick: () => {
          this.createGroup();
        },
      },
      {
        label: 'Download',
        svgJar: 'download',
        onClick: () => {
          this.download();
        },
      },
    ];
  }

  @cached
  get availablePropertyFilters(): PropertyFilterInfoForQueryFilter[] {
    const list: PropertyFilterInfoForQueryFilter[] = [];

    list.push({
      propertyName: 'giftDate',
      label: 'Gift Date',
      propertyType: 'date',
      availableOperations: ['><', '>=', '<=', '='],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        if (appliedFilter.operation === '>=') {
          queryFilter.from = appliedFilter.val;
        } else if (appliedFilter.operation === '<=') {
          queryFilter.to = appliedFilter.val;
        } else if (appliedFilter.operation === '><') {
          let dates = appliedFilter.val as string[];
          queryFilter.from = dates[0];
          queryFilter.to = dates[1];
        } else {
          queryFilter.from = appliedFilter.val;
          queryFilter.to = appliedFilter.val;
        }
      },
    });

    let contactFilterInfos =
      this.contactsCache.availablePropertyFiltersForContacts.filter(
        (x) => x.custom.includeOnAnyReport,
      );
    for (let filterInfo of contactFilterInfos) {
      list.push({
        propertyName: filterInfo.propertyName,
        label: filterInfo.label,
        propertyType: filterInfo.propertyType,
        availableOperations: filterInfo.availableOperations,
        listOptions: filterInfo.listOptions,
        listIsSingleSelection: filterInfo.listIsSingleSelection,
        applyToQueryFilter: async (
          appliedFilter: AppliedPropertyFilter,
          queryFilter: any,
        ) => {
          let contactIds = (
            await this.contactsCache.filter([appliedFilter])
          ).map((x) => x.id);
          if (contactIds.length === 0) {
            contactIds = ['-1']; // since no records match the contact filter, force the api to return 0 records
          }
          queryFilter.contactIds = ContactIdsIntersection(
            queryFilter.contactIds,
            contactIds,
          );
        },
      });
    }

    return list;
  }

  get totalAverageDonations(): number {
    var numberDonations = this.totalNumberDonations;

    if (numberDonations === 0) {
      return 0;
    }

    return this.totalTotalDonations / numberDonations;
  }

  @cached
  get itemsByState(): { state: string; amount: number }[] {
    const byState: IIndexable<number> = {};

    this.items?.forEach((item: any) => {
      let state = item.contact.state;

      if (isEmpty(state)) {
        state = 'Other';
      }

      state = state.trim();

      if (byState[state] == null) {
        byState[state] = 0;
      }

      byState[state] += item.record.totalDonations;
    });

    return Object.keys(byState).map((state) => {
      return {
        state: state,
        amount: byState[state],
      };
    });
  }

  get locationAddress(): string | undefined {
    const filter = this.appliedFilters?.find(
      (f) => f.propertyName === 'contactLocation',
    );
    return (filter?.val as DistanceFromVal)?.location;
  }

  @cached
  get sortedItems(): any[] {
    return sortByProperties(this.items, this.sortBy);
  }

  @cached
  get sortedItemsByState(): { state: string; amount: number }[] {
    return sortByProperties(this.itemsByState, this.itemsByStateSortBy);
  }

  get totalNumberDonations(): number {
    if (this.items == null) {
      return 0;
    }

    return this.items.reduce((sum, i) => sum + i.record.numberDonations, 0);
  }

  get totalTotalDonations(): number {
    if (this.items == null) {
      return 0;
    }

    return this.items.reduce((sum, i) => sum + i.record.totalDonations, 0);
  }

  createGroup = () => {
    const contactIds = this.sortedItems.map((item) => item.contact.id);
    this.router.transitionTo('funds.fund.groups.create', {
      queryParams: { contactIds: contactIds.join('-') },
    });
  };

  download = async () => {
    const headerRow = [
      'Contact',
      '# Donations',
      'Average Donation',
      'Last Donation Date',
      'Last Donation Amount',
      'Pledge Amount',
      'Pledge Frequency',
      'Total Donations',
      '---',
    ];

    const headerInfo = await this.contactsExport.addContactsHeaderColumns(
      headerRow,
      this.contactsCache.contacts,
    );
    const rows = [headerRow];

    for (let item of this.sortedItems) {
      const row = [
        item.contact.name,
        item.record.numberDonations,
        parseFloat(item.record.avgDonation.toString()).toFixed(2),
        item.record.lastDonationDate,
        item.record.lastDonationAmount,
        item.record.pledge?.amount,
        item.record.pledge?.frequencyLabel,
        item.record.totalDonations,
        '',
      ];

      await this.contactsExport.addContactInfoToRow(
        row,
        item.contact,
        headerInfo,
      );

      rows.push(row);
    }

    this.store.createRecord('contact-download').save();

    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });

    FileSaver.saveAs(blob, 'donors-by-time-frame.csv', true);
  };

  handleFiltersChanged = (currentFilters: AppliedPropertyFilter[]) => {
    this.appliedFilters = currentFilters;
    this.serializeFilters();
    this.load();
  };

  templateDidInsert = () => {
    this.deserializeFilters();
    this.load();
  };

  templateWillDestroy = () => {
    this.reset();
  };

  updateSortBy = (property: string) => {
    this.sortBy = this.getSortByForProperty(property, this.sortBy);
  };

  updateItemsByStateSortBy = (property: string) => {
    this.itemsByStateSortBy = this.getSortByForProperty(
      property,
      this.itemsByStateSortBy,
    );
  };

  deserializeFilters() {
    this.appliedFilters = deserializeAppliedFilters(this.filters);
  }

  serializeFilters() {
    this.filters = serializeAppliedFilters(this.appliedFilters);
  }

  getSortByForProperty(property: string, current: string[]): string[] {
    const [currentProperty, currentDirection] = current[0].split(':');

    if (currentProperty === property && currentDirection === 'asc') {
      return [`${property}:desc`];
    }

    return [`${property}:asc`];
  }

  reset() {
    this.appliedFilters = undefined;
    this.error = undefined;
    this.filters = undefined;
    this.loading = true;
    this.items = undefined;
  }
}
